import { Badge, Box, Flex, Heading } from '@radix-ui/themes';
import { AdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineFiringTab } from 'components/common/settings-dialog//machine-tabs/firing.tab';
import { MachineGeneralTab } from 'components/common/settings-dialog//machine-tabs/general.tab';
import { MachineMaintenanceTab } from 'components/common/settings-dialog/machine-tabs/maintenance.tab';
import { MachineProjectorTab } from 'components/common/settings-dialog/machine-tabs/projector.tab';
import { MachineSwitchTab } from 'components/common/settings-dialog/machine-tabs/switch.tab';
import { MachineTrackingDeviceTab } from 'components/common/settings-dialog/machine-tabs/tracking-device.tab';
import { CommonTabs } from 'components/common/tabs';
import { MachineContext } from 'contexts/machine.context';
import { MachineSettingsTabKey } from 'enums/settings';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  initialTab?: MachineSettingsTabKey;
  onClose: () => void;
}

export const MachineSection = (props: IProps) => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { machine, update: updateMachine } = useContext(MachineContext);

  const [tab, setTab] = useState(
    props.initialTab ?? MachineSettingsTabKey.General
  );

  return (
    <ErrorBoundary identifier="MachineTab">
      <Box>
        <Flex gap={RADIX.FLEX.GAP.SM}>
          <Heading size={RADIX.HEADING.SIZE.MD}>{t('common.machine')}</Heading>

          {machine.machineID && (
            <Badge color={RADIX.COLOR.WARNING}>{machine.machineID}</Badge>
          )}
        </Flex>

        <CommonTabs
          value={tab}
          onValueChange={(v) => setTab(v as MachineSettingsTabKey)}
          tabs={[
            {
              value: MachineSettingsTabKey.General,
              label: 'settings.general',
              content: (
                <MachineGeneralTab
                  onSave={updateMachine}
                  onClose={props.onClose}
                />
              ),
            },
            {
              value: MachineSettingsTabKey.Projector,
              label: 'settings.projector',
              content: <MachineProjectorTab onSave={updateMachine} />,
            },
            {
              value: MachineSettingsTabKey.Firing,
              label: 'settings.firing',
              content: <MachineFiringTab />,
            },
            {
              value: MachineSettingsTabKey.TrackingDevice,
              label: 'settings.tracking-device',
              content: <MachineTrackingDeviceTab onSave={updateMachine} />,
            },
            {
              value: MachineSettingsTabKey.Maintenance,
              label: 'settings.maintenance',
              content: <MachineMaintenanceTab />,
            },
            {
              value: MachineSettingsTabKey.Switch,
              label: 'settings.switch-machine',
              labelIcon: <AdminIcon />,
              content: <MachineSwitchTab onClose={props.onClose} />,
              invisible: !userRoleFeatureFlags.controls.switchMachine,
            },
          ]}
          scrollOptions={{ background: '--gray-2' }}
          scroll
        />
      </Box>
    </ErrorBoundary>
  );
};
